/**
 * Mock data source for the public-facing webinar surfaces — registration
 * landing pages and past-webinar detail pages. Swap this file (or branch on
 * env) for a real HTTP client when the API lands. Consumers go through
 * `webinar-public.service.ts`.
 */

export interface WebinarPublicSpeaker {
  name: string
  role: string
  avatar: string
}

export interface WebinarPublicSummary {
  id: string
  title: string
  host: { name: string; title: string }
  date: string
  time: string
  timezone: string
  banner: string
  description: string
  capacity: number
  registeredCount: number
  topics: string[]
  speakers: WebinarPublicSpeaker[]
}

export interface WebinarRecording {
  id: string
  name: string
  duration: string
  size: string
  thumb: string
}

export interface WebinarAttendanceTick {
  t: string
  count: number
}

export interface WebinarPastDetail {
  id: string
  title: string
  date: string
  startTime: string
  endTime: string
  durationMin: number
  status: 'completed' | 'cancelled'
  registrations: number
  attended: number
  peakConcurrent: number
  avgWatchTime: string
  satisfaction: number
  recordings: WebinarRecording[]
  attendanceCurve: WebinarAttendanceTick[]
}

export interface WebinarRegistrationEntry {
  id: string
  name: string
  email: string
  date: string
  status: 'Attended' | 'Registered'
}

const PUBLIC_SUMMARY: Omit<WebinarPublicSummary, 'id'> = {
  title: 'Next-Generation Video Conferencing: AI & Beyond',
  host: { name: 'Rama Krishna Yadla', title: 'Senior Product Designer, InMeet' },
  date: 'Wednesday, July 23, 2025',
  time: '10:00 AM – 11:30 AM IST',
  timezone: 'Asia/Kolkata',
  banner: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=1200',
  description: "Join us for an exclusive deep-dive into how AI is reshaping video conferencing. We'll cover real-time transcription, intelligent noise suppression, virtual backgrounds powered by machine learning, and what the next generation of remote collaboration looks like.",
  capacity: 500,
  registeredCount: 312,
  topics: [
    'AI-powered transcription',
    'Noise suppression & clarity',
    'Virtual backgrounds',
    'Meeting analytics',
    'Security & compliance',
  ],
  speakers: [
    { name: 'Rama Krishna Yadla', role: 'Product Design Lead',  avatar: 'https://randomuser.me/api/portraits/men/32.jpg' },
    { name: 'Sarah Mitchell',     role: 'AI Research Engineer', avatar: 'https://randomuser.me/api/portraits/women/44.jpg' },
  ],
}

const PAST_DETAIL: Omit<WebinarPastDetail, 'id'> = {
  title: 'Advanced Product Design Systems',
  date: 'Apr 10, 2024',
  startTime: '10:00 AM',
  endTime: '11:30 AM',
  durationMin: 90,
  status: 'completed',
  registrations: 248,
  attended: 189,
  peakConcurrent: 163,
  avgWatchTime: '54 min',
  satisfaction: 4.6,
  recordings: [
    { id: 'r1', name: 'Full Webinar Recording', duration: '1:28:30', size: '680 MB', thumb: 'https://picsum.photos/320/180?random=20' },
  ],
  attendanceCurve: [
    { t: '0 min', count: 0 }, { t: '5 min', count: 62 }, { t: '15 min', count: 145 },
    { t: '30 min', count: 163 }, { t: '45 min', count: 158 }, { t: '60 min', count: 147 },
    { t: '75 min', count: 130 }, { t: '90 min', count: 98 },
  ],
}

const REG_NAMES = [
  'Alice Johnson', 'Bob Kumar', 'Carla Mendes', 'Dev Patel', 'Elena Torres',
  'Frank Liu', 'Grace Kim', 'Henry Okonkwo', 'Iris Novak', 'Jake Brown',
  'Karen Smith', 'Leo Zhang',
]

const REGISTRATIONS: WebinarRegistrationEntry[] = REG_NAMES.map((name, i) => ({
  id: `reg${i}`,
  name,
  email: `user${i + 1}@example.com`,
  date: 'Apr 8, 2024',
  status: i < 9 ? 'Attended' : 'Registered',
}))

export const webinarPublicRepo = {
  async getPublicSummary(id: string): Promise<WebinarPublicSummary> {
    return { id, ...PUBLIC_SUMMARY }
  },
  async getPastDetail(id: string): Promise<WebinarPastDetail> {
    return { id, ...PAST_DETAIL }
  },
  async listRegistrations(_webinarId: string): Promise<WebinarRegistrationEntry[]> {
    return REGISTRATIONS.map((r) => ({ ...r }))
  },
}
